import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import React, { useEffect, useState } from 'react';
import {
    setBaseCode,
    setFCodeCategory,
    setFirstLevelOptions,
    setFirstLevelSelection,
    setSecondLevelOptions,
    setSecondLevelSelection,
    setShowFCodeModal,
    setThirdLevelOptions,
    setThirdLevelSelection,
} from '../../../store/assessment.slice';
import { ICD10 } from '../../../Models/Shared/ICD10.model';
import { copyObject } from '../../../utils/common';
import { setEngagementDraft } from '../../../store/documentEngagement.slice';
import { SummaryNoteTypeEnum } from '../../../Enum/SummaryNoteTypeEnum';
import { DocumentEngagement } from '../../../Models/DocumentEngagements/DocumentEngagement.model';

const FCodeModal = (props: { values: DocumentEngagement }) => {
    const dispatch = useAppDispatch();
    const {
        baseCode,
        fCodesForBHDiagnostic,
        fCodeCategory,
        firstLevelOptions,
        firstLevelSelection,
        secondLevelOptions,
        secondLevelSelection,
        showFCodeModal,
        thirdLevelOptions,
        thirdLevelSelection,
    } = useSelector((state: RootState) => state.assessment);
    const { engagementDraft } = useSelector((state: RootState) => state.documentEngagement);

    const [availableBaseCodes, setAvailableBaseCodes] = useState<ICD10[]>([]);
    const [showError, setShowError] = useState(false);

    const fCodeCategories = [
        { label: 'F01 - F09 Mental disorders due to known physiological conditions', value: 'F01_F09' },
        { label: 'F10 - F19 Mental and behavioral disorders due to psychoactive substance use', value: 'F10_F19' },
        { label: 'F20 - F29 Schizophrenia, schizotypal, and delusional disorders', value: 'F20_F29' },
        { label: 'F30 - F39 Mood [affective] disorders', value: 'F30_F39' },
        { label: 'F40 - F48 Anxiety, dissociative, stress-related, somatoform, and other nonpsychotic mental disorders', value: 'F40_F48' },
        { label: 'F50 - F59 Behavioral syndromes associated with physiological disturbances and physical factors', value: 'F50_F59' },
        { label: 'F60 - F69 Disorders of adult personality and behavior', value: 'F60_F69' },
        { label: 'F70 - F79 Intellectual disabilities', value: 'F70_F79' },
        { label: 'F80 - F89 Pervasive and specific developmental disorders', value: 'F80_F89' },
        { label: 'F90 - F98 Behavioral and emotional disorders with onset usually occurring in childhood and adolescence', value: 'F90_F98' },
        { label: 'F99 Unspecified mental disorder', value: 'F99' },
    ];

    // Filter F-codes based on selected category
    useEffect(() => {
        if (fCodeCategory && fCodesForBHDiagnostic?.length) {
            // Parse the category range
            const parts = fCodeCategory.split('_');

            // Filter codes that match the category range
            let codesInRange: ICD10[] = [];

            if (parts.length === 1 && parts[0] === 'F99') {
                // Special case for F99
                codesInRange = fCodesForBHDiagnostic.filter((code) => code.Code.startsWith('F99'));
            } else if (parts.length === 2) {
                const startCode = parts[0]; // e.g., "F01"
                const endCode = parts[1]; // e.g., "F09"

                // Extract numbers for range
                const startNum = parseInt(startCode.substring(1));
                const endNum = parseInt(endCode.substring(1));

                // Filter codes that fall within the range
                codesInRange = fCodesForBHDiagnostic.filter((code) => {
                    if (!code.Code.startsWith('F')) return false;

                    // Get the number part (e.g., "01" from "F01.5")
                    const codeBase = code.Code.split('.')[0];
                    if (codeBase.length < 3) return false;

                    const codeNum = parseInt(codeBase.substring(1));
                    return codeNum >= startNum && codeNum <= endNum;
                });
            }

            // Extract unique base codes (e.g., F01, F02)
            const baseCodesMap = new Map<string, ICD10>();

            codesInRange.forEach((code) => {
                const baseCodeStr = code.Code.split('.')[0];

                // If this base code hasn't been added yet
                if (!baseCodesMap.has(baseCodeStr)) {
                    // Try to find an exact match for the base code
                    const exactMatch = fCodesForBHDiagnostic.find((c) => c.Code === baseCodeStr);

                    if (exactMatch) {
                        baseCodesMap.set(baseCodeStr, exactMatch);
                    } else {
                        // Otherwise use this code but modify to just show the base
                        baseCodesMap.set(baseCodeStr, {
                            ...code,
                            Code: baseCodeStr,
                            Name: code.Name.split(',')[0], // Simplify name
                        });
                    }
                }
            });

            setAvailableBaseCodes(Array.from(baseCodesMap.values()));

            // Reset selections when category changes
            dispatch(setBaseCode(''));
            dispatch(setFirstLevelSelection(''));
            dispatch(setFirstLevelOptions([]));
            dispatch(setSecondLevelSelection(''));
            dispatch(setSecondLevelOptions([]));
            dispatch(setThirdLevelSelection(''));
            dispatch(setThirdLevelOptions([]));
        }
    }, [dispatch, fCodeCategory, fCodesForBHDiagnostic]);

    // When base F-code is selected, filter for first level options
    useEffect(() => {
        if (baseCode && fCodesForBHDiagnostic?.length) {
            // Filter for codes that start with the base code and have one level after decimal (e.g., F01.1)
            const filteredOptions = fCodesForBHDiagnostic.filter((diagnosis) => {
                if (!diagnosis.Code.startsWith(baseCode)) return false;
                if (diagnosis.Code === baseCode) return false;

                const parts = diagnosis.Code.split('.');
                if (parts.length !== 2) return false;

                // Only include codes with one digit after decimal (e.g., F01.1, not F01.11)
                return parts[1].length === 1;
            });

            // Remove duplicates by Code
            const uniqueOptions = Array.from(new Map(filteredOptions.map((item) => [item.Code, item])).values());

            dispatch(setFirstLevelOptions(uniqueOptions));

            // Reset selections
            dispatch(setFirstLevelSelection(''));
            dispatch(setSecondLevelOptions([]));
            dispatch(setSecondLevelSelection(''));
            dispatch(setThirdLevelOptions([]));
            dispatch(setThirdLevelSelection(''));
        }
    }, [dispatch, baseCode, fCodesForBHDiagnostic]);

    // When first level selection changes, filter for second level options
    useEffect(() => {
        if (firstLevelSelection && fCodesForBHDiagnostic?.length) {
            // Filter for codes that extend the first level by one digit
            const filteredOptions = fCodesForBHDiagnostic.filter((diagnosis) => {
                if (!diagnosis.Code.startsWith(firstLevelSelection)) return false;
                if (diagnosis.Code === firstLevelSelection) return false;

                const parts = diagnosis.Code.split('.');
                if (parts.length !== 2) return false;

                const firstLevelParts = firstLevelSelection.split('.');
                if (firstLevelParts.length !== 2) return false;

                // Only include codes with one more digit after decimal
                // e.g., if first level is F01.5, include F01.51, F01.52, etc.
                return parts[1].startsWith(firstLevelParts[1]) && parts[1].length === firstLevelParts[1].length + 1;
            });

            dispatch(setSecondLevelOptions(filteredOptions));

            // Reset downstream selections
            dispatch(setSecondLevelSelection(''));
            dispatch(setThirdLevelOptions([]));
            dispatch(setThirdLevelSelection(''));
        }
    }, [dispatch, firstLevelSelection, fCodesForBHDiagnostic]);

    // When second level selection changes, filter for third level options
    useEffect(() => {
        if (secondLevelSelection && fCodesForBHDiagnostic?.length) {
            // Filter for codes that extend the second level
            const filteredOptions = fCodesForBHDiagnostic.filter((diagnosis) => {
                if (!diagnosis.Code.startsWith(secondLevelSelection)) return false;
                if (diagnosis.Code === secondLevelSelection) return false;

                // Include any more specific codes (longer than the selected code)
                return true;
            });

            dispatch(setThirdLevelOptions(filteredOptions));
            dispatch(setThirdLevelSelection(''));
        }
    }, [dispatch, secondLevelSelection, fCodesForBHDiagnostic]);

    // Handlers
    const handleChangeCategory = (event: SelectChangeEvent) => {
        const selectedCategory = event.target.value;
        dispatch(setFCodeCategory(selectedCategory));
        setShowError(false);
    };

    const handleChangeBaseCode = (event: SelectChangeEvent) => {
        const selectedCode = event.target.value;
        dispatch(setBaseCode(selectedCode));
        setShowError(false);
    };

    const handleChangeFirstLevel = (event: SelectChangeEvent) => {
        const selected = event.target.value;
        dispatch(setFirstLevelSelection(selected));
    };

    const handleChangeSecondLevel = (event: SelectChangeEvent) => {
        const selected = event.target.value;
        dispatch(setSecondLevelSelection(selected));
    };

    const handleChangeThirdLevel = (event: SelectChangeEvent) => {
        const selected = event.target.value;
        dispatch(setThirdLevelSelection(selected));
    };

    const onClose = () => {
        dispatch(setShowFCodeModal(false));
    };

    const handleAddFcode = () => {
        // Get the most specific code selected
        const selectedCode = thirdLevelSelection || secondLevelSelection || firstLevelSelection || baseCode;

        if (!selectedCode) {
            setShowError(true);
            return;
        }

        // Find the diagnosis object for the selected code
        let diagnosisObject: ICD10 | null = null;

        if (thirdLevelSelection) {
            diagnosisObject = thirdLevelOptions.find((d) => d.Code === thirdLevelSelection) || null;
        } else if (secondLevelSelection) {
            diagnosisObject = secondLevelOptions.find((d) => d.Code === secondLevelSelection) || null;
        } else if (firstLevelSelection) {
            diagnosisObject = firstLevelOptions.find((d) => d.Code === firstLevelSelection) || null;
        } else if (baseCode) {
            diagnosisObject = fCodesForBHDiagnostic.find((d) => d.Code === baseCode) || null;
        }

        const copy = copyObject(props.values);
        const newNote = {
            Id: copy.MemberId,
            CptCode: engagementDraft.DiagnosticStatus?.CPTcode?.Code || 'T1016',
            CategoryGroupId: '',
            ReferenceIds: [] as string[],
            Zcode: diagnosisObject.Code,
            Note: `${diagnosisObject.Code} - ${diagnosisObject.Name}`,
            Type: SummaryNoteTypeEnum.BHDiagnostic,
            IsConfirm: true,
            OrderBy: 1,
            DisplayName: 'BH Diagnostic',
        };
        const updatedSummaryNotes = [newNote, ...copy.SummaryNotes];
        copy.SummaryNotes = updatedSummaryNotes;
        dispatch(setEngagementDraft(copy));

        onClose();
    };

    // Get display name for selected diagnosis
    const getSelectedDiagnosisDisplayText = () => {
        if (thirdLevelSelection) {
            const diag = thirdLevelOptions.find((d) => d.Code === thirdLevelSelection);
            return diag ? `${diag.Code} - ${diag.Name}` : thirdLevelSelection;
        }

        if (secondLevelSelection) {
            const diag = secondLevelOptions.find((d) => d.Code === secondLevelSelection);
            return diag ? `${diag.Code} - ${diag.Name}` : secondLevelSelection;
        }

        if (firstLevelSelection) {
            const diag = firstLevelOptions.find((d) => d.Code === firstLevelSelection);
            return diag ? `${diag.Code} - ${diag.Name}` : firstLevelSelection;
        }

        if (baseCode) {
            const diag = availableBaseCodes.find((d) => d.Code === baseCode);
            return diag ? `${diag.Code} - ${diag.Name}` : baseCode;
        }

        return 'No diagnosis selected';
    };

    return (
        <Dialog open={showFCodeModal} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Add F-Code</DialogTitle>
            <DialogContent>
                <Box width="100%" display="flex" justifyContent="center" alignItems="center" mt={2}>
                    <Stack spacing={3} width="100%">
                        {/* F-Code Category Selection */}
                        <FormControl fullWidth>
                            <InputLabel id="f-code-category-label">F-Code Group</InputLabel>
                            <Select
                                labelId="f-code-category-label"
                                id="f-code-category-select"
                                value={fCodeCategory || ''}
                                label="F-Code Group"
                                onChange={handleChangeCategory}
                                error={showError && !fCodeCategory}
                            >
                                <MenuItem value="" disabled>
                                    Select Group
                                </MenuItem>
                                {fCodeCategories.map((category) => (
                                    <MenuItem key={category.value} value={category.value}>
                                        {category.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {showError && !fCodeCategory && <FormHelperText error>Please select a category</FormHelperText>}
                        </FormControl>

                        {/* Base F-Code Selection */}
                        {fCodeCategory && availableBaseCodes.length > 0 && (
                            <FormControl fullWidth>
                                <InputLabel id="base-f-code-label">Base F-Code</InputLabel>
                                <Select
                                    labelId="base-f-code-label"
                                    id="base-f-code-select"
                                    value={baseCode}
                                    label="Base F-Code"
                                    onChange={handleChangeBaseCode}
                                    error={showError && !baseCode}
                                >
                                    <MenuItem value="" disabled>
                                        Select Base F-Code
                                    </MenuItem>
                                    {availableBaseCodes.map((diagnosis) => (
                                        <MenuItem key={diagnosis.Code} value={diagnosis.Code}>
                                            {`${diagnosis.Code} - ${diagnosis.Name}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {showError && !baseCode && <FormHelperText error>Please select a base F-code</FormHelperText>}
                            </FormControl>
                        )}

                        {/* First Level Selection (e.g., F01.1, F01.2, etc.) */}
                        {baseCode && firstLevelOptions.length > 0 && (
                            <FormControl fullWidth>
                                <InputLabel id="first-level-label">First Level</InputLabel>
                                <Select
                                    labelId="first-level-label"
                                    id="first-level-select"
                                    value={firstLevelSelection}
                                    label="First Level"
                                    onChange={handleChangeFirstLevel}
                                >
                                    <MenuItem value="" disabled>
                                        Select First Level
                                    </MenuItem>
                                    {firstLevelOptions.map((option) => (
                                        <MenuItem key={option.Code} value={option.Code}>
                                            {`${option.Code} - ${option.Name}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Second Level Selection (e.g., F01.10, F01.11, etc.) */}
                        {firstLevelSelection && secondLevelOptions.length > 0 && (
                            <FormControl fullWidth>
                                <InputLabel id="second-level-label">Second Level</InputLabel>
                                <Select
                                    labelId="second-level-label"
                                    id="second-level-select"
                                    value={secondLevelSelection}
                                    label="Second Level"
                                    onChange={handleChangeSecondLevel}
                                >
                                    <MenuItem value="" disabled>
                                        Select Second Level
                                    </MenuItem>
                                    {secondLevelOptions.map((option) => (
                                        <MenuItem key={option.Code} value={option.Code}>
                                            {`${option.Code} - ${option.Name}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Third Level Selection */}
                        {secondLevelSelection && thirdLevelOptions.length > 0 && (
                            <FormControl fullWidth>
                                <InputLabel id="third-level-label">Third Level</InputLabel>
                                <Select
                                    labelId="third-level-label"
                                    id="third-level-select"
                                    value={thirdLevelSelection}
                                    label="Third Level"
                                    onChange={handleChangeThirdLevel}
                                >
                                    <MenuItem value="" disabled>
                                        Select Third Level
                                    </MenuItem>
                                    {thirdLevelOptions.map((option) => (
                                        <MenuItem key={option.Code} value={option.Code}>
                                            {`${option.Code} - ${option.Name}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Selection Summary */}
                        {baseCode && (
                            <Box sx={{ bgcolor: 'background.paper', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Selected Diagnosis:
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {getSelectedDiagnosisDisplayText()}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button sx={{ minWidth: 120 }} variant="outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button sx={{ minWidth: 120 }} variant="contained" onClick={handleAddFcode} disabled={!baseCode}>
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FCodeModal;
